import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, Download, Loader2, FileImage, FileText, FileSpreadsheet, Presentation, X, FileUp } from "lucide-react";
import { api } from "../../lib/api";
import { handlePdfError } from "../../lib/pdf";
import { useAuth } from "../../context/AuthContext";
import { opCost, creditLabel, tierName } from "../../lib/pricing";
import { useOnline } from "../../hooks/usePWA";
import { Button } from "../ui/button";
import BrandingBar from "./BrandingBar";
import ToolIntro from "./ToolIntro";
import PreviewUnlock from "./PreviewUnlock";

const IMG_EXT = ["png", "jpg", "jpeg", "webp", "bmp", "gif"];
const OFFICE_EXT = ["docx", "xlsx", "pptx"];
const ext = (n) => (n.split(".").pop() || "").toLowerCase();
const isOffice = (n) => OFFICE_EXT.includes(ext(n));

export default function ConverterTab() {
  const [files, setFiles] = useState([]);
  const [branding, setBranding] = useState({ watermark: "", brand_color: "" });
  const [drag, setDrag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState(null);
  const inputRef = useRef(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const online = useOnline();

  const hasOfficeFile = files.some((f) => isOffice(f.name));
  const kind = hasOfficeFile ? "convert:office" : "convert:images";
  const cost = opCost(user, kind);

  const addFiles = (list) => {
    const arr = Array.from(list);
    const valid = arr.filter((f) => IMG_EXT.includes(ext(f.name)) || isOffice(f.name));
    if (valid.length !== arr.length) toast.error("Some files skipped", { description: "Only images, Word, Excel & PowerPoint are supported." });
    const combined = [...files, ...valid];
    const hasOffice = combined.some((f) => isOffice(f.name));
    if (hasOffice && combined.length > 1) {
      toast.error("Documents convert one at a time", { description: "Upload a single Word/Excel/PowerPoint file, or only images." });
      setFiles(valid.filter((f) => isOffice(f.name)).slice(0, 1));
      return;
    }
    setFiles(combined);
  };

  const onDrop = (e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); };
  const remove = (i) => setFiles((f) => f.filter((_, idx) => idx !== i));

  const fileIcon = (name) => {
    const e = ext(name);
    if (e === "xlsx") return <FileSpreadsheet className="h-5 w-5 text-green-600" />;
    if (e === "pptx") return <Presentation className="h-5 w-5 text-orange-600" />;
    if (e === "docx") return <FileText className="h-5 w-5 text-blue-600" />;
    return <FileImage className="h-5 w-5 text-emerald-600" />;
  };

  const generate = async () => {
    if (!files.length) { toast.error("Add a file first"); return; }
    setLoading(true);
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      if (branding.watermark) form.append("watermark", branding.watermark);
      if (branding.brand_color) form.append("brand_color", branding.brand_color);
      if (branding.watermark_position) form.append("watermark_position", branding.watermark_position);
      if (branding.watermark_opacity != null) form.append("watermark_opacity", branding.watermark_opacity);
      const res = await api.post("/pdf/convert", form, { headers: { "Content-Type": "multipart/form-data" } });
      setStage(res.data);
    } catch (e) {
      if (handlePdfError(e) === "no-credits") navigate("/buy-credits");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <ToolIntro
        Icon={FileUp}
        title="File → PDF Converter"
        desc="Combine images into one PDF, or convert a Word, Excel or PowerPoint file to PDF. Office files are Standard tier; large docs add 1 credit per 30 pages over 30."
        accepts="PNG · JPG · WEBP · DOCX · XLSX · PPTX"
        tier={tierName(kind)}
        costLabel={creditLabel(cost)}
      />
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        data-testid="converter-dropzone"
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-12 text-center transition-colors ${
          drag ? "border-indigo-500 bg-indigo-50" : "border-slate-300 bg-white hover:border-indigo-400 hover:bg-slate-50"
        }`}
      >
        <input
          ref={inputRef}
          data-testid="file-upload-input"
          type="file"
          multiple
          accept=".png,.jpg,.jpeg,.webp,.bmp,.gif,.docx,.xlsx,.pptx"
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
        />
        <div className="h-14 w-14 rounded-2xl bg-indigo-100 flex items-center justify-center mx-auto mb-4">
          <UploadCloud className="h-7 w-7 text-indigo-600" />
        </div>
        <p className="font-display text-lg font-semibold">Drop files here or click to browse</p>
        <p className="text-slate-500 text-sm mt-1">Combine images into one PDF, or convert Word, Excel & PowerPoint files.</p>
        <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
          {["PNG", "JPG", "WEBP", "DOCX", "XLSX", "PPTX"].map((b) => (
            <span key={b} className="text-xs font-mono bg-slate-100 text-slate-600 px-2 py-1 rounded">{b}</span>
          ))}
        </div>
      </div>

      {files.length > 0 && (
        <div className="mt-6 space-y-2" data-testid="converter-file-list">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3">
              {fileIcon(f.name)}
              <span className="flex-1 text-sm truncate">{f.name}</span>
              <span className="text-xs text-slate-400 font-mono">{(f.size / 1024).toFixed(0)} KB</span>
              <button onClick={() => remove(i)} className="text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6">
        <BrandingBar value={branding} onChange={setBranding} />
      </div>

      <div className="mt-2 flex justify-center">
        <Button
          data-testid="generate-pdf-button"
          onClick={generate}
          disabled={loading || !files.length || !online}
          className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-8 h-12"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          Create preview
        </Button>
      </div>

      {stage && <PreviewUnlock stage={stage} onClose={() => setStage(null)} onUnlocked={() => { setStage(null); setFiles([]); }} />}
    </div>
  );
}
